// ImpersonationBanner — sticky top app-wide когда активна impersonation-сессия
// (admin «войти как пользователь», rules/api-layer.md §8i). Нельзя закрыть —
// всегда виден, пока админ работает под чужим аккаунтом.
//
// «Завершить» = полный logout текущей (impersonated) сессии + редирект на login.
// Better Auth НЕ держит стек сессий, поэтому вернуться в свой админский аккаунт
// автоматически нельзя — админ заходит в admin-панель заново (это норма; админ
// нажимает stop в самой панели или просто логинится). Серверный stop из админки
// дополнительно заносит сессию в revoked-blacklist (preHandler выкидывает из web).

import { Button } from "@studyhub/ui";
import { IconUserExclamation } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { authClient } from "@/lib/auth-client";
import { broadcastAuthEvent, useAuth } from "@/providers/auth-provider";

export function ImpersonationBanner(): React.ReactElement | null {
	const { t } = useTranslation("auth");
	const { isImpersonated, isOffline, user } = useAuth();

	if (!isImpersonated || isOffline) return null;

	async function handleExit(): Promise<void> {
		await authClient.logout();
		broadcastAuthEvent("logout");
		// Full reload — чистая гидратация (сброс tRPC-кэша + Bearer удалён logout'ом).
		window.location.replace("/auth/login");
	}

	const label = user?.email
		? t("impersonation.banner", { email: user.email })
		: t("impersonation.bannerNoEmail");

	return (
		<div
			role="status"
			className="flex items-center gap-3 border-b border-warning/30 bg-warning/15 px-4 py-2.5 backdrop-blur-md"
			// Собственный верхний отступ для `BannerStack`: когда баннер первый в
			// стопке, контейнер поднимает его padding-top до safe-area, но не ниже
			// этого значения — иначе `py-2.5` терялся бы и текст съезжал вверх.
			style={{ "--sh-banner-pt": "0.625rem" } as React.CSSProperties}
		>
			<IconUserExclamation size={16} className="shrink-0 text-warning" />
			<span className="text-sm font-semibold text-foreground">{label}</span>
			<div className="ml-auto">
				<Button
					size="sm"
					variant="outline"
					className="cursor-pointer"
					onClick={() => void handleExit()}
				>
					{t("impersonation.exit")}
				</Button>
			</div>
		</div>
	);
}
